import {
  PAYMENT_METHOD_LABEL,
  PAYMENT_MSG,
  PaymentMethod,
  overdueText,
  type Company,
  type Invoice,
  type OpenInvoiceList,
  type Payment,
  type PaymentAllocationInput,
  type InvoiceState,
} from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, ApiError, messageOf } from "../../api.js";
import { PartnerField } from "../../components/PartnerPickerDrawer.jsx";

/** 入金の{customer}（id・名前・敬称） */
export type PickedClient = { id: string; name: string; honorific: string };
import { DateInput } from "../../components/DateInput.jsx";
import { NumInput } from "../../components/NumInput.jsx";
import { BackButton, differs, useBackNav, usePageName } from "../../components/BackButton.jsx";
import { ErrorText, Field, Help, useFieldIds } from "../../components/ui.jsx";
import { t, useLabels } from "../../labels.js";
import { md, todayJst, yen } from "../../lib/num.js";
import { PAYMENTS_KEY } from "./PaymentsPage.jsx";

/** 配分の表の1行。available＝この入金を除いた請求書の残（直すときは、この入金が当てていた分を足し戻す） */
type Row = {
  documentId: string;
  documentNo: string | null;
  subject: string;
  dueDate: string | null;
  total: number;
  available: number;
  state: InvoiceState;
  overdueDays: number;
  amount: number;
  fee: number;
  discount: number;
};

/** 額を上から（期限の古い順に）埋める（DB の fn_payment_suggest と同じ並び・同じ埋め方） */
function fillTopDown(rows: Row[], amount: number): Row[] {
  let rest = Math.max(0, amount);
  return rows.map((r) => {
    const a = Math.min(r.available, rest);
    rest -= a;
    return { ...r, amount: a, fee: 0, discount: 0 };
  });
}

/** 入金の動いたあとに読み直すもの（残の数字は請求書・ホーム・案件の画面にも出る） */
export function invalidatePaymentViews(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: PAYMENTS_KEY });
  void qc.invalidateQueries({ queryKey: ["payment"] });
  void qc.invalidateQueries({ queryKey: ["invoices"] });
  void qc.invalidateQueries({ queryKey: ["invoice"] });
  void qc.invalidateQueries({ queryKey: ["home"] });
  void qc.invalidateQueries({ queryKey: ["group"] });
  void qc.invalidateQueries({ queryKey: ["open-invoices"] });
}

/** 入金を登録（/payments/new。?invoice= で開くとその請求書に全額を当てた形） */
export function PaymentNewPage() {
  const [sp] = useSearchParams();
  const invoiceId = sp.get("invoice");
  const inv = useQuery({ queryKey: ["invoice", invoiceId], queryFn: () => api<Invoice>("GET", `/invoices/${invoiceId}`), enabled: !!invoiceId, retry: false });
  if (invoiceId && inv.isPending) return <p className="hint">読み込んでいます…</p>;
  if (invoiceId && inv.isError) return <ErrorText>{messageOf(inv.error)}</ErrorText>;
  const i = inv.data;
  return (
    <PaymentForm
      mode="new"
      initialPartner={i ? { id: i.client.id, name: i.client.name, honorific: i.client.honorific } : null}
      initialAmount={i?.payments ? i.payments.balance : null}
      onlyInvoice={i?.id ?? null}
    />
  );
}

/**
 * 入金の欄と配分の表（登録・直すで同じ）。
 *   上：{customer} → 入金日（今日まで）→ 額 → 方法 → メモ。下：その{customer}の残のある請求書（期限の古い順）と今回の配分。
 *   額を打つと上から自動で埋まる（配分を手で直したら、自動では埋め直さない。「上から埋め直す」で戻せる）。
 *   差額があれば「手数料として処理」「値引きとして処理（会社の上限まで）」。当てずに残した額は「まだ消し込んでいない」に残る
 */
export function PaymentForm({
  mode,
  initialPartner,
  initialAmount,
  onlyInvoice,
  payment,
  onDone,
}: {
  mode: "new" | "edit";
  initialPartner: PickedClient | null;
  initialAmount: number | null;
  /** ?invoice= で開いた：その請求書だけに全額 */
  onlyInvoice?: string | null;
  /** 直すとき：いまの入金 */
  payment?: Payment;
  onDone?: (p: Payment) => void;
}) {
  const L = useLabels();
  const nav = useNavigate();
  const qc = useQueryClient();
  const ids = useFieldIds(["receivedOn", "amount", "method", "memo"] as const);
  const today = todayJst();
  const [partner, setPartner] = useState<PickedClient | null>(initialPartner);
  const [receivedOn, setReceivedOn] = useState(payment?.receivedOn ?? today);
  const [amount, setAmount] = useState<number | null>(payment?.amount ?? initialAmount);
  const [method, setMethod] = useState<PaymentMethod>(payment?.method ?? "bank_transfer");
  const [memo, setMemo] = useState(payment?.memo ?? "");
  const [rows, setRows] = useState<Row[] | null>(null);
  const [touched, setTouched] = useState(mode === "edit");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  // 戻る（#98、docs/37。登録のときだけ）：直前の画面があればそこ。無ければ 入金の一覧。入れかけは確かめる
  const first = useRef({ partner: initialPartner?.id ?? null, amount: initialAmount, memo: payment?.memo ?? "" });
  const back = useBackNav({ to: "/payments", label: "入金" }, mode === "new" && differs({ partner: partner?.id ?? null, amount, memo }, first.current));
  usePageName(mode === "new" ? "入金登録" : null);
  // 二度押し・通信のやり直しで入金が2件にならない（API は同じキーの2回目に1回目の答えを返す）
  const idemKey = useRef(`p${crypto.randomUUID().replace(/-/g, "")}`);

  const company = useQuery({ queryKey: ["company"], queryFn: () => api<Company>("GET", "/company") });
  const limit = payment?.shortfallDiscountLimit ?? company.data?.shortfallDiscountLimit ?? 1000;
  const open = useQuery({
    queryKey: ["open-invoices", partner?.id],
    queryFn: () => api<OpenInvoiceList>("GET", `/partners/${partner!.id}/open-invoices`),
    enabled: !!partner,
  });

  // 表の行：残のある請求書 ＋（直すとき）この入金が当てている請求書（残に この入金の分を足し戻す）
  useEffect(() => {
    if (!open.data) return;
    const mine = new Map((payment?.allocations ?? []).map((a) => [a.documentId, a]));
    const base: Row[] = open.data.items.map((x) => {
      const m = mine.get(x.documentId);
      return {
        documentId: x.documentId, documentNo: x.documentNo, subject: x.subject, dueDate: x.dueDate, total: x.total,
        available: x.balance + (m ? m.amount + m.fee + m.discount : 0), state: x.state, overdueDays: x.overdueDays,
        amount: m?.amount ?? 0, fee: m?.fee ?? 0, discount: m?.discount ?? 0,
      };
    });
    for (const a of payment?.allocations ?? []) {
      if (base.some((r) => r.documentId === a.documentId)) continue;
      base.push({
        documentId: a.documentId, documentNo: a.documentNo, subject: a.subject, dueDate: a.dueDate, total: a.invoiceTotal,
        available: a.balance + a.amount + a.fee + a.discount, state: a.state, overdueDays: 0, amount: a.amount, fee: a.fee, discount: a.discount,
      });
    }
    base.sort((a, b) => (a.dueDate ?? "9999").localeCompare(b.dueDate ?? "9999") || (a.documentNo ?? "").localeCompare(b.documentNo ?? ""));
    if (mode === "edit") setRows(base);
    else if (onlyInvoice) setRows(base.map((r) => (r.documentId === onlyInvoice ? { ...r, amount: Math.min(r.available, amount ?? 0) } : r)));
    else setRows(fillTopDown(base, amount ?? 0));
    // 顧客を変えたときだけ組み直す（額の変化は下の effect）
  }, [open.data]);

  // 額を打つ → 上から埋める（手で配分を直したあとはしない）
  useEffect(() => {
    if (touched || !rows || mode === "edit") return;
    if (onlyInvoice) {
      setRows((rs) => rs && rs.map((r) => (r.documentId === onlyInvoice ? { ...r, amount: Math.min(r.available, amount ?? 0), fee: 0, discount: 0 } : r)));
      return;
    }
    setRows((rs) => rs && fillTopDown(rs, amount ?? 0));
  }, [amount]);

  const sumCash = useMemo(() => (rows ?? []).reduce((s, r) => s + r.amount, 0), [rows]);
  const rest = (amount ?? 0) - sumCash;
  const overRow = (rows ?? []).find((r) => r.amount + r.fee + r.discount > r.available);
  const setRow = (id: string, patch: Partial<Row>) => {
    setTouched(true);
    setRows((rs) => rs && rs.map((r) => (r.documentId === id ? { ...r, ...patch } : r)));
  };

  const submit = async () => {
    setError(undefined);
    if (!partner) return setError(t(PAYMENT_MSG.partnerRequired));
    if (!amount || amount < 1) return setError(PAYMENT_MSG.amountInvalid);
    if (receivedOn > today) return setError(PAYMENT_MSG.futureDate);
    if (rest < 0) return setError(PAYMENT_MSG.overAllocated);
    if (overRow) return setError(PAYMENT_MSG.overInvoice);
    const allocations: PaymentAllocationInput[] = (rows ?? [])
      .filter((r) => r.amount + r.fee + r.discount > 0)
      .map((r) => ({ documentId: r.documentId, amount: r.amount, fee: r.fee, discount: r.discount }));
    setBusy(true);
    try {
      const p =
        mode === "new"
          ? await api<Payment>("POST", "/payments", { partnerId: partner.id, receivedOn, amount, method, memo: memo.trim() || undefined, allocations }, { "idempotency-key": idemKey.current })
          : await api<Payment>("PUT", `/payments/${payment!.id}`, { receivedOn, method, memo: memo.trim() || null, allocations });
      invalidatePaymentViews(qc);
      qc.setQueryData(["payment", p.id], p);
      if (onDone) onDone(p);
      else nav(`/payments/${p.id}`, { replace: true, state: { notice: mode === "new" ? PAYMENT_MSG.created : PAYMENT_MSG.updated } });
    } catch (e) {
      setError(messageOf(e));
      if (e instanceof ApiError && e.status >= 500) idemKey.current = `p${crypto.randomUUID().replace(/-/g, "")}`;
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {mode === "new" ? (
        <>
          <BackButton back={back} />
          {back.dialog}
          <div className="hrow">
            <h1>入金登録</h1>
          </div>
        </>
      ) : null}
      <section className="paper payform" data-testid="payment-form">
        {partner ? (
          <div className="row" style={{ alignItems: "baseline", gap: 12 }}>
            <span className="hint">{L.customer}</span>
            <b data-testid="payment-partner">{partner.name}</b> <span className="muted">{partner.honorific}</span>
            {mode === "new" && !onlyInvoice ? (
              <button type="button" className="small" onClick={() => { setPartner(null); setRows(null); setTouched(false); }}>変更</button>
            ) : null}
          </div>
        ) : (
          <PartnerField role="customer" label={L.customer} value={null} autoFocus onPick={(c) => { setPartner({ id: c.id, name: c.name, honorific: c.honorific }); setTouched(false); }} testId="payment-client-field" />
        )}
        <div className="row payrow" style={{ gap: 16, flexWrap: "wrap", marginTop: 12 }}>
          <Field id={ids.receivedOn} label="入金日" style={{ flex: "0 0 180px" }}>
            <DateInput id={ids.receivedOn} label="入金日" max={today} clearable={false} value={receivedOn} onChange={(v) => v && setReceivedOn(v)} testId="payment-date" />
          </Field>
          <div className="field" style={{ flex: "0 0 200px" }}>
            <label htmlFor={ids.amount}>入金の額（円）</label>
            {mode === "edit" ? (
              <div className="inp ro num" id={ids.amount}>{yen(payment!.amount)}</div>
            ) : (
              <NumInput id={ids.amount} kind="int" value={amount} onChange={setAmount} data-testid="payment-amount" />
            )}
          </div>
          <Field id={ids.method} label="方法" style={{ flex: "0 0 160px" }}>
            <select id={ids.method} className="inp" value={method} onChange={(e) => setMethod(PaymentMethod.parse(e.target.value))} data-testid="payment-method">
              {PaymentMethod.options.map((m) => (
                <option key={m} value={m}>{PAYMENT_METHOD_LABEL[m]}</option>
              ))}
            </select>
          </Field>
          <Field id={ids.memo} label="メモ（社内だけ）" style={{ flex: "1 1 240px" }}>
            <input id={ids.memo} className="inp" value={memo} maxLength={1000} onChange={(e) => setMemo(e.target.value)} />
          </Field>
        </div>
        {mode === "edit" ? <p className="hint" style={{ margin: "4px 0 0" }}>額と{L.customer}は修正できません。違っていたら、この入金を消して登録し直します。</p> : null}
      </section>

      {partner ? (
        <section style={{ marginTop: 20 }}>
          <div className="hrow">
            <h2 style={{ fontSize: "var(--fs-19)", margin: 0 }}>
              どの請求書に当てるか
              <Help testId="help-alloc" text={`${L.customer}の残のある請求書を、お支払期限の古い順に並べています。額を打つと上から自動で埋まります。修正することもできます。当てずに残した額は「まだ消し込んでいない入金」（前受金）として残り、次の請求書に当てられます。差額を値引きとして処理できる額には上限があり、超えた分は「一部入金」のまま残ります。`} />
            </h2>
            <span className="sp" />
            {touched && mode === "new" ? (
              <button type="button" className="small" onClick={() => { setTouched(false); setRows((rs) => rs && fillTopDown(rs, amount ?? 0)); }}>上から再配分</button>
            ) : null}
          </div>
          {open.isError ? <ErrorText>{messageOf(open.error)}</ErrorText> : null}
          {!rows ? (
            <p className="hint">読み込んでいます…</p>
          ) : rows.length === 0 ? (
            <p className="hint" data-testid="no-open-invoices">この{L.customer}に残のある請求書はありません。</p>
          ) : (
            <div className="tbl-wrap">
              <table className="list payalloc" data-testid="alloc-table">
                <thead>
                  <tr>
                    <th>番号</th>
                    <th>件名</th>
                    <th>お支払期限</th>
                    <th className="amt">請求額</th>
                    <th className="amt">残</th>
                    <th className="amt">今回当てる額</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const short = r.available - r.amount - r.fee - r.discount;
                    const canSettle = r.amount > 0 && short > 0 && rest === 0;
                    return (
                      <tr key={r.documentId} data-testid="alloc-row" data-doc={r.documentNo ?? ""}>
                        <td className="num">{r.documentNo}</td>
                        <td>{r.subject || <span className="muted">（件名なし）</span>}</td>
                        <td className="num">
                          {r.dueDate ? md(r.dueDate) : <span className="muted">—</span>}
                          {r.state === "overdue" && r.overdueDays > 0 ? <div className="shu" style={{ fontSize: 14 }}>{overdueText(r.overdueDays)}</div> : null}
                        </td>
                        <td className="amt num">{yen(r.total)}</td>
                        <td className="amt num">{yen(r.available)}</td>
                        <td className="amt">
                          <NumInput kind="int" value={r.amount} onChange={(v) => setRow(r.documentId, { amount: Math.max(0, v ?? 0), fee: 0, discount: 0 })} aria-label={`${r.documentNo ?? ""} に当てる額`} style={{ width: 140 }} data-testid="alloc-amount" />
                          {r.fee > 0 ? (
                            <div className="hint" data-testid="alloc-fee">
                              手数料として処理 <span className="num">{yen(r.fee)}</span> 円 <button type="button" className="btn text" onClick={() => setRow(r.documentId, { fee: 0 })}>取消</button>
                            </div>
                          ) : null}
                          {r.discount > 0 ? (
                            <div className="hint" data-testid="alloc-discount">
                              値引きとして処理 <span className="num">{yen(r.discount)}</span> 円 <button type="button" className="btn text" onClick={() => setRow(r.documentId, { discount: 0 })}>取消</button>
                            </div>
                          ) : null}
                          {canSettle ? (
                            <div className="settle">
                              <button type="button" className="small" onClick={() => setRow(r.documentId, { fee: short })} data-testid="settle-fee">{PAYMENT_MSG.feeButton(short)}</button>
                              {short <= limit ? (
                                <button type="button" className="small" onClick={() => setRow(r.documentId, { discount: short })} data-testid="settle-discount">{PAYMENT_MSG.discountButton(short)}</button>
                              ) : (
                                <span className="hint" data-testid="discount-over-limit">値引きとして処理できるのは {yen(limit)}円までです。</span>
                              )}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="paysum" data-testid="alloc-sum">
            <span>当てた額の合計 <b className="num">{yen(sumCash)}</b> 円</span>
            <span>
              まだ当てていない額 <b className="num" data-testid="alloc-rest">{yen(Math.max(0, rest))}</b> 円
            </span>
          </div>
          {rest < 0 ? <ErrorText>{PAYMENT_MSG.overAllocated}</ErrorText> : overRow ? <ErrorText>{PAYMENT_MSG.overInvoice}</ErrorText> : null}
        </section>
      ) : null}
      <ErrorText>{error}</ErrorText>
      {/* #179：当てる請求書の表が届くまでは出さない（先に出すと、表が入った瞬間に下へずれ、届く前に押すと当てる先なしで登録される） */}
      {partner && !rows && !open.isError ? null : (
      <div className="row" style={{ gap: 12, marginTop: 8 }}>
        <button type="button" className="btn primary" disabled={busy || !partner} onClick={() => void submit()} data-testid="payment-submit">
          {mode === "new" ? "入金登録" : "修正した内容で保存"}
        </button>
        {mode === "edit" && onDone && payment ? (
          <button type="button" className="btn" onClick={() => onDone(payment)}>取消</button>
        ) : null}
      </div>
      )}
    </>
  );
}
